import { Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useEvents } from "api/events";
import { getInitialDataForSelectedSchemaConditions } from "components/Layout/Campaign/Common/CampaignRules/rules";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { ExternalLink } from "ExternalLink";
import { useMessage } from "hooks/intl";
import { get } from "lodash";
import { useState } from "react";
import { DefaultLanguageDataState } from "reduxState/translations/translationsSlice";
import {
  AchievementRuleTypes,
  AchievementTriggers,
  AvailableLimitedAchievementPeriodsType,
  FEAchievementRule,
} from "types/achievements";
import { AccordionSection } from "../../../../FormControls/AcordionSection/AccordionSection";
import { InfoBox } from "../../../InfoBox";
import {
  customEventDirectAchievementConditions,
  customEventReferralAchievementConditions,
  transactionDirectAchievementConditions,
  transactionReferralAchievementConditions,
} from "../../helpers";
import {
  getGoalBadgeDetails,
  getProgressTrackingBadgeDetails,
  getTypeAndTriggerBadgesDetails,
} from "./helpers";
import { RuleBasicSettings } from "./RuleBasicSettings/RuleBasicSettings";
import { RuleConditions } from "./RuleConditions/RuleConditions";
import { RuleCounting } from "./RuleCounting/RuleCounting";
import { RuleGoal } from "./RuleGoal/RuleGoal";
import { RuleLimit } from "./RuleLimit/RuleLimit";
import { RuleTrigger } from "./RuleTrigger/RuleTrigger";
import { RuleType } from "./RuleType/RuleType";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    sectionTitle: {
      fontSize: "16px",
      fontWeight: 500,
      color: theme.primary800,
      lineHeight: theme.spacing(12),
      display: "flex",
      alignItems: "center",
    },
    sectionSubtitle: {
      color: theme.gray500,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: theme.spacing(10),
      marginTop: theme.spacing(1),
      whiteSpace: "break-spaces",
    },
    sectionInfo: {
      color: theme.gray500,
      fontSize: "12px",
    },
    link: {
      fontSize: "14px",
      textDecoration: "underline",
    },
    bolded: {
      fontWeight: "bold",
    },
    infoBox: {
      marginBottom: theme.spacing(12),
    },
  }),
);

interface Props {
  error: any;
  activeRule: number;
  isEditMode: boolean;
  ruleData: FEAchievementRule;
  availableLanguages: DefaultLanguageDataState[];
  setFieldValue: (field: string, value: any) => void;
  setHelpPanel: React.Dispatch<React.SetStateAction<string | false>>;
}

export const Rule = ({
  error,
  ruleData,
  activeRule,
  isEditMode,
  setFieldValue,
  availableLanguages,
  setHelpPanel,
}: Props) => {
  const classes = useStyles({ isEditMode });
  const { message } = useMessage();

  const { useCustomEventSchemaDetails } = useEvents();
  const { data: selectedSchemaDetails } = useCustomEventSchemaDetails(
    ruleData.event,
  );
  const [expanded, setExpanded] = useState<string | false>("title");

  const isTransactionTrigger =
    ruleData.trigger === AchievementTriggers.TRANSACTION;
  const isDirectType = ruleData.type === AchievementRuleTypes.DIRECT;

  const selectedSchemaConditions = [
    ...(selectedSchemaDetails?.schema?.fields.map(
      ({
        name,
        type,
        description,
      }: {
        name: string;
        type: string;
        description: string;
      }) => {
        return {
          attribute: `event.body.${name}`,
          category: "customEvent",
          data: getInitialDataForSelectedSchemaConditions(type),
          label: description || name,
          operator: type === "boolean" ? "is_equal" : undefined,
          type: type,
        };
      },
    ) || []),
    ...(isDirectType
      ? customEventDirectAchievementConditions
      : customEventReferralAchievementConditions),
  ];

  const transactionConditions = isDirectType
    ? transactionDirectAchievementConditions
    : transactionReferralAchievementConditions;

  const hasError = (field: string) => !!get(error, field);

  const completeRuleData = ruleData.completeRule.isEditing
    ? ruleData.completeRule.draft
    : ruleData.completeRule;

  const typeAndTrggerBadgesDetails = getTypeAndTriggerBadgesDetails(
    ruleData,
    selectedSchemaDetails?.name || "",
    message,
  );

  const progressTrackingBadgeDetails = getProgressTrackingBadgeDetails(
    ruleData,
    message,
  );

  const goalBadgeDetails = getGoalBadgeDetails(completeRuleData, message);

  const accordionSectionsData = [
    {
      sectionId: "title",
      title: "titleAndDescription",
      hasError: hasError("translations"),
      details: (
        <RuleBasicSettings
          availableLanguages={availableLanguages}
          translations={ruleData.translations}
          activeRule={activeRule}
          errors={error}
        />
      ),
    },
    {
      sectionId: "type-trigger",
      title: "typeAndTrigger",
      subtitle: "selectAchievementTypeAndTriggerInfo",
      hasWarning: true,
      hasError: hasError("event"),
      details: (
        <>
          <Typography className={classes.sectionTitle} mb={8}>
            {message({ id: "selectAchievementType" })}
          </Typography>
          <RuleType
            type={ruleData.type}
            setFieldValue={setFieldValue}
            activeRule={activeRule}
          />
          <Typography className={classes.sectionTitle} mt={12}>
            {message({ id: "selectAchievementTrigger" })}
          </Typography>
          <Typography className={classes.sectionSubtitle} mb={8}>
            {message({
              id: "achievementActionInfo",
            })}
          </Typography>
          <RuleTrigger
            trigger={ruleData.trigger}
            setFieldValue={setFieldValue}
            activeRule={activeRule}
            event={ruleData.event}
            errors={error}
          />
        </>
      ),
      badges: typeAndTrggerBadgesDetails,
    },
    {
      sectionId: "progress-tracking",
      title: "progressTracking",
      subtitle: "progressTrackingInfo",
      hasWarning: true,
      details: (
        <RuleCounting
          aggregation={ruleData.aggregation.type}
          trigger={ruleData.trigger}
          setFieldValue={setFieldValue}
          activeRule={activeRule}
        />
      ),
      badges: progressTrackingBadgeDetails,
    },
    {
      sectionId: "goals",
      title: "goals",
      subtitle: "setTargetToCompleteThisRule",
      subtitleError: hasError("completeRule.period.type"),
      hasWarning: true,
      hasError: hasError("completeRule") || hasError("aggregation"),
      details: (
        <RuleGoal
          aggregation={ruleData.aggregation}
          setFieldValue={setFieldValue}
          activeRule={activeRule}
          completeRule={ruleData.completeRule}
          conditions={selectedSchemaConditions || []}
          trigger={ruleData.trigger}
          type={ruleData.type}
          event={ruleData.event}
          errors={error}
          schemaDetails={selectedSchemaDetails}
        />
      ),
      badges: goalBadgeDetails,
    },
    {
      sectionId: "conditions",
      title: "triggerConditionsOptional",
      subtitle: "triggerConditionsInfo",
      hasError: hasError("conditions"),
      details: (
        <RuleConditions
          activeRule={activeRule}
          conditions={
            isTransactionTrigger
              ? transactionConditions
              : selectedSchemaConditions
          }
          errors={error}
        />
      ),
    },
    {
      sectionId: "event-limit",
      title: "eventLimit",
      subtitle: "eventLimitInfo",
      hasError: hasError("limit"),
      hasWarning: true,
      details: (
        <RuleLimit
          activeRule={activeRule}
          ruleLimitType={
            ruleData?.limit?.interval
              ?.type as AvailableLimitedAchievementPeriodsType
          }
          errors={error}
        />
      ),
    },
  ];

  return (
    <div>
      {isEditMode && (
        <section className={classes.infoBox}>
          <InfoBox
            type="warning"
            text={message(
              {
                id: "editAchievementRuleWarningBoxInfo",
              },
              {
                link: (
                  <ExternalLink
                    className={classes.link}
                    path="https://help.openloyalty.io/main-features/achievements/managing-achievements"
                    data-test-id="managing-achievements-link"
                  >
                    {message({ id: "learnMore" })}
                  </ExternalLink>
                ),
                span: (chunks: React.ReactNode[]) => (
                  <span className={classes.bolded}>{chunks}</span>
                ),
              },
            )}
          />
        </section>
      )}
      <Typography className={classes.sectionTitle}>
        {message({ id: "ruleSettings" })}
      </Typography>
      <Typography className={classes.sectionSubtitle} mb={12}>
        {message({ id: "ruleSettingsInfo" })}
      </Typography>
      {accordionSectionsData.map((section, index) => {
        const idx = index + 1;

        return (
          <AccordionSection
            {...section}
            idx={idx}
            isEditMode={isEditMode}
            isExpanded={expanded === section?.sectionId}
            nextSectionId={accordionSectionsData[idx]?.sectionId}
            nextSectionButtonLabel={accordionSectionsData[idx]?.title}
            setExpanded={(panel) => {
              setExpanded(panel);
              setHelpPanel(panel);
            }}
            key={index}
            badges={section.badges}
          />
        );
      })}
    </div>
  );
};
